import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Textarea } from "@shared-ui/textarea";
import { FileCheck2, XCircle } from "lucide-react";

/** Reject stays disabled until the note has at least this many non-blank characters. */
const MIN_REJECTION_REASON_LENGTH = 5;

interface DecisionNotePanelProps {
	description: string;
	placeholder: string;
	notes: string;
	onNotesChange: (notes: string) => void;
	onApprove: () => void;
	onReject: () => void;
	isApproving: boolean;
	isRejecting: boolean;
}

/**
 * The note an admin records with an approve or reject decision, and the two
 * buttons. Each button is disabled while the other decision is in flight.
 */
export function DecisionNotePanel({
	description,
	placeholder,
	notes,
	onNotesChange,
	onApprove,
	onReject,
	isApproving,
	isRejecting,
}: DecisionNotePanelProps): React.ReactElement {
	return (
		<Card className="border-slate-200 shadow-none">
			<CardHeader>
				<CardTitle>Decision note</CardTitle>
				<CardDescription>{description}</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-4">
				<Textarea
					value={notes}
					onChange={(event) => onNotesChange(event.target.value)}
					rows={6}
					placeholder={placeholder}
				/>
				<div className="grid gap-3 sm:grid-cols-2">
					<Button
						onClick={onApprove}
						isLoading={isApproving}
						disabled={isRejecting}
					>
						<FileCheck2 data-icon="inline-start" />
						Approve
					</Button>
					<Button
						variant="outline"
						onClick={onReject}
						isLoading={isRejecting}
						disabled={
							isApproving || notes.trim().length < MIN_REJECTION_REASON_LENGTH
						}
					>
						<XCircle data-icon="inline-start" />
						Reject
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}
